import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useAuth } from "../../../../../Context/AuthContext";

const hasPrintableValue = (value) => {
  if (value === null || value === undefined) return false;

  const text = String(value).trim();
  return Boolean(text) && !["-", "null", "undefined", "غير محدد", "غير محددة"].includes(text);
};

const getCustomerValue = (order, key) =>
  order?.customer?.[key] ?? order?.[`customer_${key}`] ?? "";

const OrderCustomerUserInfoSection = ({ order, onEditCustomer }) => {
  const { hasPermission } = useAuth();
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);

  const customerName = useMemo(() => {
    const name = getCustomerValue(order, "name");
    return hasPrintableValue(name) ? name : "غير متوفر";
  }, [order]);

  const customerDetails = useMemo(() => {
    if (!order) return [];

    return [
      ["البريد الإلكتروني", getCustomerValue(order, "email")],
      ["الهاتف", getCustomerValue(order, "phone")],
      ["الهاتف 2", getCustomerValue(order, "phone2")],
      ["الهاتف 3", getCustomerValue(order, "phone3")],
      ["العنوان", getCustomerValue(order, "address")],
      ["العنوان 2", getCustomerValue(order, "address2")],
      ["العنوان 3", getCustomerValue(order, "address3")],
    ].filter(([, value]) => hasPrintableValue(value));
  }, [order]);

  if (!order) return null;

  const hasCustomer =
    Boolean(order.customer) ||
    hasPrintableValue(order.customer_name) ||
    customerDetails.length > 0;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, backgroundColor: "#fafafa" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">معلومات العميل</Typography>
            </Box>

            {hasCustomer ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      اسم العميل
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {customerName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {customerDetails.length > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        endIcon={
                          showCustomerDetails ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )
                        }
                        onClick={() => setShowCustomerDetails((current) => !current)}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {showCustomerDetails ? "إخفاء البيانات" : "عرض البيانات"}
                      </Button>
                    )}

                    {hasPermission("edit") && (
                      <Tooltip title="تعديل بيانات العميل">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={onEditCustomer}
                          aria-label="تعديل بيانات العميل"
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {customerDetails.length > 0 && (
                  <Collapse in={showCustomerDetails} timeout="auto" unmountOnExit>
                    <Divider />
                    <Grid container spacing={1.5} sx={{ p: 1.5 }}>
                      {customerDetails.map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                              {value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Collapse>
                )}
              </Box>
            ) : (
              <Typography variant="body2">لا توجد بيانات عميل</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, backgroundColor: "#fafafa" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              معلومات المستخدم
            </Typography>
            {order.user ? (
              <Grid container spacing={1.5}>
                {order.user.name && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        الاسم
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {order.user.name}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography variant="body2">لا توجد بيانات مستخدم</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderCustomerUserInfoSection;
